import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';
import { useAudioStore, type SleepTimerType } from '@/stores/audioStore';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

/**
 * Bottom-sheet for selecting a Schlaf-Timer. 5 options — 4 timer
 * durations + "Aus" (off) to clear an active timer:
 *
 *   - 15 Minuten           → setSleepTimer('15min')
 *   - 30 Minuten           → setSleepTimer('30min')
 *   - 1 Stunde             → setSleepTimer('1h')
 *   - Ende der Geschichte  → setSleepTimer('endOfStory')
 *   - Aus                  → clearSleepTimer()
 *
 * Selecting any option closes the sheet (via onOpenChange(false)). The
 * current active timer (if any) is highlighted with a dream-gold ring so
 * the user sees the selected state when re-opening the sheet mid-timer.
 *
 * Controlled open-state — the parent owns `open` so it can coordinate
 * with the Schlaf-Timer button in AudioMiniControls.
 */
interface SchlafTimerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TimerOption {
  /** null = "Aus" / off — calls clearSleepTimer. */
  type: SleepTimerType | null;
  labelKey: keyof typeof STRINGS.shell.nowPlaying.sheet.options;
}

const OPTIONS: readonly TimerOption[] = [
  { type: '15min', labelKey: '15min' },
  { type: '30min', labelKey: '30min' },
  { type: '1h', labelKey: '1h' },
  { type: 'endOfStory', labelKey: 'endOfStory' },
  { type: null, labelKey: 'off' },
] as const;

export function SchlafTimerSheet({ open, onOpenChange }: SchlafTimerSheetProps) {
  const activeType = useAudioStore((s) => s.sleepTimer?.type ?? null);
  const setSleepTimer = useAudioStore((s) => s.setSleepTimer);
  const clearSleepTimer = useAudioStore((s) => s.clearSleepTimer);

  const copy = STRINGS.shell.nowPlaying.sheet;

  const handleSelect = (type: SleepTimerType | null) => {
    if (type === null) {
      clearSleepTimer();
    } else {
      setSleepTimer(type);
    }
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader className="gap-2 px-6 pt-6">
          <SheetTitle>{copy.title}</SheetTitle>
          <SheetDescription>{copy.description}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-2 px-6 pt-4 pb-10">
          {OPTIONS.map((opt) => {
            const isActive = opt.type === activeType;
            return (
              <button
                key={opt.labelKey}
                type="button"
                onClick={() => handleSelect(opt.type)}
                className={cn(
                  'flex h-12 w-full items-center justify-center rounded-lg border border-border bg-background px-4 text-base font-medium text-foreground transition-colors hover:bg-muted',
                  isActive && 'ring-2 ring-dream-gold',
                )}
              >
                {copy.options[opt.labelKey]}
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
